import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import InvoiceDownloadIcon from '@app/assets/icons/invoice-download.svg?react';
import DotsIcon from '@app/assets/icons/dot-horizontal.svg?react';
import CancelSubscriptionIcon from '@app/assets/icons/cancel-subscription.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import { Button, PlanCard, BaseDialog } from '@app/components';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { GenericTableRow } from '@app/components/GenericTable/GenericTableRow';
import { formatPrice } from '@app/utils/utils';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useYourCurrentPlan } from './YourCurrentPlan.hooks';
import { InvoiceInterface } from '@app/models/subscription';

export const YourCurrentPlan = () => {
  const {
    businessSubscription,
    invoicesResponse,
    isLoading,
    currentPage,
    orderBy,
    orderDirection,
    handlePageChange,
    handleLimitChange,
    handleSort,
    cancelSubscription,
  } = useYourCurrentPlan();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleDownloadInvoice = (invoice: InvoiceInterface) => {
    if (invoice.invoice_pdf) {
      const link = document.createElement('a');
      link.href = invoice.invoice_pdf;
      link.download = `invoice-${invoice.number}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      await cancelSubscription();
      // Close modal and potentially redirect or show success message
      setIsModalOpen(false);
      // You might want to refresh the subscription data or redirect
      navigate('/settings?tab=plan');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      // Handle error - show toast or error message
    } finally {
      setIsCancelling(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div>
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h2 className="font-bold text-[28px]">
            <button
              onClick={() => navigate('/settings?tab=plan')}
              className="mr-2"
            >
              <ArrowBackIcon />
            </button>
            Your Current Plan
          </h2>
          <div className="flex gap-2">
            <Menu>
              <MenuButton
                className="group/user flex items-center justify-center gap-2 p-2 bg-[#F6F7FA] rounded"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsIcon />
              </MenuButton>
              <MenuItems
                transition
                anchor="left start"
                className="p-2 bg-white shadow-lg mt-2 min-w-[220px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border border-base-antiFlash"
              >
                <MenuItem>
                  <button
                    className="flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal();
                    }}
                  >
                    <CancelSubscriptionIcon className="[&_path]:stroke-system-error flex-shrink-0" />
                    <span className="flex items-center">Stop subscription</span>
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <PlanCard businessSubscription={businessSubscription} hideHeader />
          </div>
          {/*<div>
            <PlanUsageCard />
          </div>*/}
          <div className="col-span-2">
            <div className="bg-white p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-800">Invoices</h3>
            </div>

            <GenericTable
              isLoading={isLoading}
              data={
                invoicesResponse?.data
                  ?.invoices as unknown as InvoiceInterface[]
              }
              renderRow={(data) =>
                data?.map((item, index) => {
                  const amount = (item.amount_due ?? item.amount_paid) / 100;

                  return (
                    <GenericTableRow
                      key={item.id}
                      id={String(index)}
                      render={
                        <>
                          <td className="p-4 font-bold">
                            {formatPrice(amount)}
                          </td>
                          <td className="p-4 uppercase">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  item.status === 'paid'
                                    ? 'bg-green-500'
                                    : item.status === 'failed' ||
                                        item.status === 'uncollectible'
                                      ? 'bg-red-500'
                                      : 'bg-gray-400'
                                }`}
                              />
                              {item.status}
                            </div>
                          </td>
                          <td className="p-4">
                            {(item?.description as string) ??
                              'Monthly payment for subscription plan'}
                          </td>
                          <td className="p-4">
                            {item.payment_method_details?.card?.last4
                              ? `•••• •••• •••• ${item.payment_method_details.card.last4}`
                              : 'Card'}
                          </td>
                          <td className="p-4">
                            {DateTime.fromSeconds(
                              Number(item?.period_start ?? '')
                            ).toFormat("MMM dd, yyyy 'at' hh:mm a")}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleDownloadInvoice(item)}
                              className="p-2 hover:bg-gray-100 rounded transition-colors"
                              title="Download Invoice PDF"
                            >
                              <InvoiceDownloadIcon className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                            </button>
                          </td>
                        </>
                      }
                    />
                  );
                })
              }
              currentPage={currentPage}
              currentSortBy={orderBy}
              currentSortOrder={orderDirection}
              onSort={handleSort}
              headers={[
                { field: 'amount', label: 'amount', sortable: true },
                { field: 'status', label: 'status', sortable: true },
                { field: 'description', label: 'description', sortable: true },
                {
                  field: 'payment_method',
                  label: 'payment method',
                  sortable: true,
                },
                { field: 'date', label: 'date', sortable: true },
                { field: 'download', label: '', sortable: false },
              ]}
              pagination={{
                total: invoicesResponse?.data?.pagination?.totalCount ?? 0,
                handleNextPage: handlePageChange,
                handlePrevPage: handlePageChange,
                handleLimitChange: handleLimitChange,
              }}
            />
          </div>
        </div>
      </div>

      <BaseDialog open={isModalOpen} onClose={closeModal} size="max-w-md">
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-red-50 flex items-center p-2 rounded-lg">
            <CancelSubscriptionIcon className="w-6 h-6" />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg leading-tight">
              Stop subscription
            </h5>
          </div>
          <div>
            <button type="button" onClick={closeModal}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="px-6 pb-4">
          <p className="text-base-davyGray text-sm mb-4">
            Are you sure you want to stop your subscription for this business?
            You'll lose all the data you've saved until you restart it again.
          </p>
          <p className="text-base-davyGray text-sm mb-6">
            Your current subscription will be valid until{' '}
            <strong>
              {businessSubscription?.endDate
                ? DateTime.fromISO(businessSubscription.endDate).toFormat(
                    'MMMM dd, yyyy'
                  )
                : 'N/A'}
            </strong>{' '}
            and will be terminated after that.
          </p>

          <div className="flex gap-3 justify-end">
            <Button
              kind="secondary"
              onClick={closeModal}
              disabled={isCancelling}
            >
              Cancel
            </Button>
            <Button
              kind="danger"
              onClick={handleCancelSubscription}
              loading={isCancelling}
              style={{ backgroundColor: '#FF595E' }}
            >
              Stop
            </Button>
          </div>
        </div>
      </BaseDialog>
    </div>
  );
};
